'use client';

/**
 * ComposeMessageCard — registry-driven multi-channel composer card.
 *
 * Wraps `<MessengerTabsSection />` (the WhatsApp/WeChat/Chatter tab
 * compose UI) as a standalone card consumable by the registry-driven
 * `<ChatSurface />`. All compose state — channels, contacts, mutations,
 * the multi-context picker — comes from `useChatSurface()` so this
 * card stays coordinated with `<ChatThreadCard />` (sending invalidates
 * the chat fetch, the new message lands in the thread above).
 *
 * Renders nothing when the entity exposes no channels (e.g. a selection
 * with no buyer-side compose targets) — same behaviour as the legacy
 * `<ChatPanel />`'s tail.
 *
 * @see klyde_edge/composition/cards/messaging.py — compose_message CardSpec
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Frontend / Messaging
 * @layer: presentation
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { MessengerTabsSection } from './sections/messenger-tabs-section';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useChatSurface } from './chat-surface-context';
import type { ComposeContext } from './chat-surface-context';

const TYPE_LABELS: Record<string, string> = {
  vendor: 'Company',
  contact: 'Contact',
  customer: 'Customer',
  project: 'Project',
  rfq: 'RFQ',
  po: 'PO',
};

function ContextPicker({
  contexts,
  selectedIdx,
  onSelect,
}: {
  contexts: ComposeContext[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = contexts[selectedIdx];
  const filtered = query
    ? contexts.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : contexts;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="mb-3" ref={containerRef}>
      <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block mb-1">
        Post to
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : `${selected?.label ?? ''}`}
          placeholder="Search..."
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className="w-full text-xs px-2 py-1.5 border border-border/50 rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
        {!open && selected && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            {TYPE_LABELS[selected.type] ?? selected.type}
          </span>
        )}
        {open && filtered.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-32 overflow-auto">
            {filtered.map((ctx) => {
              const realIdx = contexts.indexOf(ctx);
              return (
                <button
                  key={ctx.sid}
                  onClick={() => { onSelect(realIdx); setOpen(false); setQuery(''); }}
                  className={cn(
                    'w-full text-left px-2 py-1.5 text-xs hover:bg-accent/50 flex items-center justify-between',
                    realIdx === selectedIdx && 'bg-accent/30',
                  )}
                >
                  <span className="truncate">{ctx.label}</span>
                  <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                    {TYPE_LABELS[ctx.type] ?? ctx.type}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function ComposeMessageCard() {
  const {
    isLoading,
    contacts,
    composeContexts,
    channels,
    selectedContextIdx,
    setSelectedContextIdx,
    sendWhatsApp,
    postChatter,
    isSendingWhatsApp,
    isPostingChatter,
  } = useChatSurface();

  // Scene-only export — before the no-channels early return (Rules of
  // Hooks). The compose surface state: channels + selected context.
  useCardExport(useMemo(() => ({
    label: 'Compose',
    content_key: 'compose_message',
    surface: 'chat' as const,
    status: 'loaded' as const,
    query_key: null,
    text: channels.length ? `Channels: ${channels.join(', ')}` : 'No channels',
    json: {
      channels,
      context_count: composeContexts.length,
      selected_context_idx: selectedContextIdx ?? null,
      contact_count: contacts?.length ?? 0,
    },
  }), [channels, composeContexts, selectedContextIdx, contacts]));

  if (channels.length === 0) return null;

  return (
    <MessengerTabsSection
      channels={channels}
      // Expanded by default in the chat surface — primary task here is
      // to respond. Collapse-by-default was a holdover from a denser
      // legacy layout where the composer competed with the message list
      // for vertical space; in the cards-everywhere chat surface the
      // composer is its own card and that pressure no longer applies.
      collapsedByDefault={false}
      contacts={contacts}
      contactsLoading={isLoading}
      onSendWhatsApp={channels.includes('whatsapp') ? sendWhatsApp : undefined}
      onPostToChatter={channels.includes('chatter') ? postChatter : undefined}
      isSendingWhatsApp={isSendingWhatsApp}
      isPostingChatter={isPostingChatter}
      contextPicker={composeContexts.length > 1 ? (
        <ContextPicker
          contexts={composeContexts}
          selectedIdx={selectedContextIdx}
          onSelect={setSelectedContextIdx}
        />
      ) : undefined}
    />
  );
}
